import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './ProdIngredients.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const blankForm = { ingredient_id: '', quantity: '', fraction: '1000', no_of_portions: '10' };

const ProdIngredients = () => {
  const { user, pageAccess } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const product_id = location.state?.product_id;
  const freeze_definition = location.state?.freeze_definition || 'N';
  const searchForm = location.state?.searchForm;
  const isFrozen = freeze_definition === 'Y';
  const sub = localStorage.getItem('selectedSubsidiaryKey') || 'LH';
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [ingredients, setIngredients] = useState([]);
  const [ingLov, setIngLov] = useState([]);
  const [productName, setProductName] = useState('');
  const [productCost, setProductCost] = useState(null);
  const [canEditFrozen, setCanEditFrozen] = useState(false);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [ingInput, setIngInput] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (user && pageAccess) {
        const ok = await pageAccess('freezeProductDefinition', user.role);
        setCanEditFrozen(ok);
      }
      await Promise.all([fetchIngredients(), fetchIngLov(), fetchProductInfo(), fetchCost()]);
    })();
  }, [product_id]);

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchIngredients = async () => {
    try {
      const res = await axios.get(`${API_URL}/prod-ingredients/byProdId/${product_id}`, auth());
      setIngredients(res.data);
    } catch {}
  };

  const fetchIngLov = async () => {
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/lhitems/${sub}`, auth());
      setIngLov(res.data);
    } catch {}
  };

  const fetchProductInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/products/byid/${product_id}`, auth());
      setProductName(res.data?.name || '');
    } catch {}
  };

  const fetchCost = async () => {
    try {
      const res = await axios.get(`${API_URL}/products/productcost/${product_id}`, auth());
      setProductCost(res.data?.product_cost ?? null);
    } catch {}
  };

  const handleIngInput = (e) => {
    const val = e.target.value;
    setIngInput(val);
    if (!val.trim()) {
      setFiltered([]);
      setShowDrop(false);
      setForm(prev => ({ ...prev, ingredient_id: '' }));
      return;
    }
    const f = ingLov.filter(i => i.name.toLowerCase().includes(val.toLowerCase()));
    setFiltered(f.slice(0, 12));
    setShowDrop(f.length > 0);
  };

  const selectIngredient = (item) => {
    setIngInput(item.name);
    setForm(prev => ({ ...prev, ingredient_id: item.id }));
    setShowDrop(false);
    setFiltered([]);
  };

  const openAdd = () => {
    setForm(blankForm);
    setIngInput('');
    setEditing(false);
    setError('');
    setModal('form');
  };

  const openEdit = (ing) => {
    setForm({
      ingredient_id: ing.ingredient_id,
      quantity: ing.quantity,
      fraction: ing.fraction,
      no_of_portions: ing.no_of_portions,
    });
    setIngInput(ing.ingredient_name || '');
    setEditing(true);
    setError('');
    setModal('form');
  };

  const closeModal = () => { setModal(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ingredient_id) { setError('Please select an ingredient.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/prod-ingredients/${product_id}/${form.ingredient_id}`, form, auth());
      } else {
        await axios.post(`${API_URL}/prod-ingredients`, { ...form, product_id }, auth());
      }
      closeModal();
      fetchIngredients();
      fetchCost();
    } catch {
      setError('Failed to save ingredient.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ingredient_id) => {
    if (!window.confirm('Delete this ingredient?')) return;
    try {
      await axios.delete(`${API_URL}/prod-ingredients/${product_id}/${ingredient_id}`, auth());
      fetchIngredients();
      fetchCost();
    } catch {}
  };

  return (
    <div className="pi-page">

      {/* Back */}
      <button className="pi-back-btn" onClick={() => navigate('/setup/products', { state: { searchForm } })}>
        ← Back to Products
      </button>

      {/* Info bar */}
      <div className="pi-info-bar">
        <div className="pi-info-name">{productName || `Product #${product_id}`}</div>
        <div className="pi-info-cost">
          Cost: <strong>{productCost != null ? `₹${parseFloat(productCost).toFixed(2)}` : '—'}</strong>
        </div>
        <button className="pi-refresh-btn" onClick={() => fetchCost()} title="Refresh cost">↺ Refresh</button>
        <button className="btn-add-user" onClick={openAdd} disabled={isFrozen && !canEditFrozen}>
          <span className="icon">+</span> Add Ingredient
        </button>
      </div>

      {/* Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>Quantity</th>
              <th>Fraction</th>
              <th>No of Portions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.length === 0 ? (
              <tr><td colSpan={5} className="no-data">No ingredients defined.</td></tr>
            ) : ingredients.map(ing => (
              <tr key={ing.ingredient_id}>
                <td>{ing.ingredient_name}</td>
                <td>{ing.quantity}</td>
                <td>{ing.fraction}</td>
                <td>{ing.no_of_portions}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => openEdit(ing)} disabled={isFrozen && !canEditFrozen} title="Edit">✏️</button>
                    <button className="btn-delete" onClick={() => handleDelete(ing.ingredient_id)} disabled={isFrozen && !canEditFrozen} title="Delete">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-content--wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Ingredient' : 'Add Ingredient'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {error && <div className="pi-form-error">{error}</div>}
              <div className="pi-form-body">

                {/* Ingredient autocomplete */}
                <div className="form-group">
                  <label>Ingredient *</label>
                  <div className="pi-ac-wrap" ref={dropRef}>
                    <input
                      className="form-input"
                      value={ingInput}
                      onChange={handleIngInput}
                      onFocus={() => ingInput && filtered.length > 0 && setShowDrop(true)}
                      placeholder="Type ingredient name…"
                      disabled={editing}
                      autoComplete="off"
                      required={!editing}
                    />
                    {showDrop && !editing && (
                      <ul className="pi-ac-dropdown">
                        {filtered.map(i => (
                          <li key={i.id} className="pi-ac-option" onMouseDown={() => selectIngredient(i)}>
                            {i.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="pi-form-row3">
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input required type="number" step="any" min="0" className="form-input"
                      value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} placeholder="Quantity" />
                  </div>
                  <div className="form-group">
                    <label>Fraction *</label>
                    <input required type="number" step="any" min="1" className="form-input"
                      value={form.fraction} onChange={e => setForm(p => ({ ...p, fraction: e.target.value }))} placeholder="Fraction" />
                  </div>
                  <div className="form-group">
                    <label>No of Portions *</label>
                    <input required type="number" step="any" min="1" className="form-input"
                      value={form.no_of_portions} onChange={e => setForm(p => ({ ...p, no_of_portions: e.target.value }))} placeholder="No of Portions" />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update Ingredient' : 'Add Ingredient'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProdIngredients;
